import { Injectable } from '@angular/core';
import { Observable, throwError, firstValueFrom } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { retry, catchError, timeout } from 'rxjs/operators';
import { DEV_BASE_URL,  PROD_BASE_URL } from './api.url';
import { StakeHolderModel, Comment, IrisUser, Participation, IpResponse , UserConnection, BoosterQuota, BoosterHistoryItem} from '../models/crypto.model';
import { HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class IrisAppService {

  BASE_URL:string;
  ENV_PROD: boolean = environment.production;
 
  constructor(private http: HttpClient) {
    this.BASE_URL = DEV_BASE_URL;
    if(this.ENV_PROD==true){
      this.BASE_URL = PROD_BASE_URL;
    }
  }
  
  // Http Headers
  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  httpPriceOptions = {
    headers: new HttpHeaders({
      'accept': 'application/json',
      'X-API-Key': environment.moralisApiKey,
    }),
  };

  getIrisTokenCurrentPrice(): Observable<any> {
    return this.http
    .get<any>(
      'https://deep-index.moralis.io/api/v2/erc20/0x7B9F36a2F331EcE03a7483D2713Cfd806f9bEef2/price?chain=bsc&exchange=PancakeSwapv2',
      this.httpPriceOptions)
    .pipe(retry(1), catchError(this.errorHandl));
  }

   // GET
  getByURL(url : string): Observable<any> {
    return this.http
    .get<StakeHolderModel>(this.BASE_URL + url)
    .pipe(retry(1), catchError(this.errorHandl));
  }
  
  getUserConnection(ip:string): Observable<UserConnection> {
    return this.http.get<UserConnection>( this.BASE_URL + 'ConnectionTables/GetConnectionTable/?ip='+ip)
      .pipe(
        retry(1),
        catchError(this.errorHandl)  // Ensure errorHandler is properly defined
      );
  }
  
  getIpAddress(): Observable<IpResponse> {
    return this.http.get<IpResponse>( 'https://api64.ipify.org?format=json')
      .pipe(
        retry(1),
        catchError(this.errorHandl)  // Ensure errorHandler is properly defined
      );
  }

  getAirdropList(): Observable<IpResponse> {
    return this.http.get<IpResponse>('assets/airdrop.json')
      .pipe(
        retry(1),
        catchError(this.errorHandl)  // Ensure errorHandler is properly defined
      );
  }
  
  // GET
  getStakeholderByAddressAndCode(address : string, code : string): Observable<StakeHolderModel> {
    return this.http
    .get<StakeHolderModel>(this.BASE_URL + 'Stakeholders/GetStakeholder?address=' + address + '&code=' + code)
    .pipe(retry(1), catchError(this.errorHandl));
  }

  getComments(projectId?: number): Observable<Comment[]> {
    const url = projectId != null
      ? this.BASE_URL + 'Comments?projectId=' + projectId
      : this.BASE_URL + 'Comments';
    return this.http
    .get<Comment[]>(url)
    .pipe(retry(1), catchError(this.errorHandl));
  }

  // POST
  generateAppKey(data : any): Observable<StakeHolderModel> {      
      return this.http
      .post<StakeHolderModel>(
          this.BASE_URL + 'Stakeholders/ActivateStakeholder',
          JSON.stringify(data),
          this.httpOptions
      )
      .pipe(retry(1), catchError(this.errorHandl));
  }

  postByUrl(data : any, url : string): Observable<any> {
    return this.http
    .post<any>(
        this.BASE_URL + url,
        JSON.stringify(data),
        this.httpOptions
    )
    .pipe(retry(1), catchError(this.errorHandl));
}

  disconnectWallet(address : any): Observable<StakeHolderModel> {      
    return this.http
    .post<any>(
        this.BASE_URL + 'ConnectionTables/DeleteItem?address='+address,
        JSON.stringify(address),
        this.httpOptions
    )
    .pipe(retry(1), catchError(this.errorHandl));
}

    // POST
    desableAppKey(data : any): Observable<StakeHolderModel> {
        return this.http
        .post<StakeHolderModel>(
            this.BASE_URL + 'Stakeholders/DesableStakeholderKey',
            JSON.stringify(data),
            this.httpOptions
        )
        .pipe(retry(1), catchError(this.errorHandl));
    }

    // POST — journal de volume (token-swap.page.ts / buy-iris.page.ts), fire-and-forget côté
    // appelant : ne doit jamais bloquer/casser l'UX d'un swap déjà réussi on-chain.
    postTokenSwapLog(data : any): Observable<any> {
      return this.http
      .post<any>(
          this.BASE_URL + 'TokenSwapLogs',
          JSON.stringify(data),
          this.httpOptions
      )
      .pipe(catchError(this.errorHandl));
    }

    // POST — création d'un ordre limite (token-swap-limit.component.ts) après un mint réussi
    // sur le NonfungiblePositionManager Uniswap V3 — fire-and-forget, même philosophie que
    // postTokenSwapLog : la chaîne reste la source de vérité, cet enregistrement ne sert qu'à
    // retrouver "mes ordres" dans l'onglet Orders.
    createTokenSwapOrder(data: any): Observable<any> {
      return this.http
      .post<any>(
          this.BASE_URL + 'TokenSwapOrders',
          JSON.stringify(data),
          this.httpOptions
      )
      .pipe(catchError(this.errorHandl));
    }

    getTokenSwapOrders(walletAddress: string, network: string): Observable<any[]> {
      return this.http
        .get<any[]>(this.BASE_URL + 'TokenSwapOrders/by-wallet?address=' + encodeURIComponent(walletAddress) + '&network=' + encodeURIComponent(network))
        .pipe(catchError(this.errorHandl));
    }

    // Informationnel seulement — le statut affiché dans l'onglet Orders vient toujours d'une
    // lecture fraîche positions(tokenId) côté client, jamais de ce flag.
    closeTokenSwapOrder(id: number, txHash: string): Observable<any> {
      return this.http
      .post<any>(
          this.BASE_URL + 'TokenSwapOrders/' + id + '/close',
          JSON.stringify({ txHash }),
          this.httpOptions
      )
      .pipe(catchError(this.errorHandl));
    }

    // POST
    postComment(data : any): Observable<Comment> {
      return this.http
      .post<Comment>(
          this.BASE_URL + 'Comments',
          JSON.stringify(data),
          this.httpOptions
      )
      .pipe(retry(1), catchError(this.errorHandl));
    }

    postUserInfo(data : any): Observable<IrisUser> {      
      return this.http
      .post<IrisUser>(
          this.BASE_URL + 'IrisUsers/',
          JSON.stringify(data),
          this.httpOptions
      )
      .pipe(retry(1), catchError(this.errorHandl));
    }

    postParticipation(data : any): Observable<Participation> {
      return this.http
      .post<Participation>(
          this.BASE_URL + 'Participations/',
          JSON.stringify(data),
          this.httpOptions
      )
      .pipe(retry(1), catchError(this.errorHandl));
    }

    getIrisUserByWallet(address: string): Observable<any> {
      return this.http
        .get<any>(this.BASE_URL + 'IrisUsers/by-wallet?address=' + encodeURIComponent(address))
        .pipe(catchError(this.errorHandl));
    }

    getIrisTokenApiPrice(): Observable<{ price: number }> {
      return this.http
        .get<{ price: number }>(this.BASE_URL + 'Prices/iristoken')
        .pipe(catchError(this.errorHandl));
    }

    // Boosters de parrainage (2026-08-18, MVP) — voir IrisUsersController.GetBoosterQuota/SendBooster.
    getBoosterQuota(userId: number): Observable<BoosterQuota> {
      return this.http
        .get<BoosterQuota>(this.BASE_URL + 'IrisUsers/' + userId + '/boosters')
        .pipe(catchError(this.errorHandl));
    }

    sendBooster(userId: number, friendUserId: number): Observable<any> {
      return this.http
        .post<any>(this.BASE_URL + 'IrisUsers/' + userId + '/boosters', { FriendUserId: friendUserId }, this.httpOptions)
        .pipe(catchError(this.errorHandl));
    }

    getBoosterHistory(userId: number): Observable<BoosterHistoryItem[]> {
      return this.http
        .get<BoosterHistoryItem[]>(this.BASE_URL + 'IrisUsers/' + userId + '/boosters/history')
        .pipe(catchError(this.errorHandl));
    }

    // walletAuthHeaders : voir Web3Service.signWalletAuthHeaders() — preuve de possession du
    // wallet, requise côté API en plus de la clé API partagée (voir WalletAuthService côté IrisApi).
    creditReferrerOnStake(userId: number, walletAuthHeaders: Record<string, string>): Observable<any> {
      return this.http
        .post<any>(this.BASE_URL + 'IrisUsers/' + userId + '/credit-referrer', null, {
          headers: new HttpHeaders({ 'Content-Type': 'application/json', ...walletAuthHeaders }),
        })
        .pipe(catchError(this.errorHandl));
    }

    // 3e déclencheur de crédit parrain (avec wallet-connect et stake→Premium/SH) — voir
    // P2PVerificationService côté IrisApi, qui revérifie DIRECTEMENT sur le contrat IrisP2P
    // (jamais confiance dans le fait que l'app affirme qu'un trade a réussi).
    creditReferrerOnP2P(userId: number, walletAuthHeaders: Record<string, string>): Observable<any> {
      return this.http
        .post<any>(this.BASE_URL + 'IrisUsers/' + userId + '/credit-referrer-p2p', null, {
          headers: new HttpHeaders({ 'Content-Type': 'application/json', ...walletAuthHeaders }),
        })
        .pipe(catchError(this.errorHandl));
    }

    updateWallet(userId: number, walletAddress: string, isPremium: boolean, isSpecialHolder: boolean, walletAuthHeaders: Record<string, string>): Observable<any> {
      return this.http
        .put(
          this.BASE_URL + 'IrisUsers/' + userId + '/wallet',
          JSON.stringify({ walletAddress, isPremium, isSpecialHolder }),
          { headers: new HttpHeaders({ 'Content-Type': 'application/json', ...walletAuthHeaders }) }
        )
        .pipe(catchError(this.errorHandl));
    }

    registerDevice(irisUserId: number, channel: 'android' | 'ios', token: string, invitedByCode: string = ''): Observable<any> {
      return this.http
        .post(
          this.BASE_URL + 'NotificationDevices/Register',
          JSON.stringify({ irisUserId, channel, token, invitedByCode }),
          this.httpOptions
        )
        .pipe(catchError(this.errorHandl));
    }

  // Error handling — must be an arrow function to preserve `this`
  errorHandl = (error: any) => {
    const status = error?.status ?? 'unknown';
    const msg = error?.message ?? 'unknown error';
    console.log(`Error Code: ${status}\nMessage: ${msg}`);
    const errorMessage = `Error Code: ${status}\nMessage: ${msg}`;
    return throwError(() => errorMessage);
  };  

  message(msg: string, panelClass: string = '', verticalPosition: any = undefined) {
    
  }

  getData(ressourse:any): Observable<any> {
    return this.http.get(ressourse).pipe(
      catchError(this.handleError)
    );
  }

  private handleError(error: any): Observable<never> {
    console.log('An error occurred:', error);
    return throwError('Something went wrong; please try again later.');
  }

  // ── Client-side cache (localStorage) ────────────────────────────────────────
  // Toujours appeler l'API en premier — le localStorage est un fallback hors-ligne uniquement.
  // IrisApi a son propre cache mémoire (invalidé par IrisWeb à chaque Create/Edit/Delete),
  // donc les appels sont rapides et le contenu est toujours frais.
  async fetchCached<T = any>(url: string): Promise<T> {
    const key = 'iris_cache_' + url;
    let entry: { data: T } | null = null;
    try {
      const raw = localStorage.getItem(key);
      if (raw) entry = JSON.parse(raw);
    } catch { /* cache corrompu */ }

    try {
      const data = await firstValueFrom(this.http.get<T>(this.BASE_URL + url).pipe(timeout(8_000)));
      try { localStorage.setItem(key, JSON.stringify({ data })); } catch { /* quota */ }
      return data;
    } catch {
      if (entry) return entry.data;
      throw new Error('No data available');
    }
  }

  clearDataCache() {
    Object.keys(localStorage)
      .filter(k => k.startsWith('iris_cache_'))
      .forEach(k => localStorage.removeItem(k));
  }
}
